import { useState } from 'react';
import { isBefore, addMinutes } from 'date-fns';
import { useToast } from '@/hooks/use-toast';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Spinner } from '@/components/ui/spinner';
import { DateTimePicker } from '@/components/ui/date-time-picker';

interface TemporaryDisarmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDisarm: (date: Date) => Promise<void>;
  isDisarming: boolean;
}

export function TemporaryDisarmDialog({
  open,
  onOpenChange,
  onDisarm,
  isDisarming,
}: TemporaryDisarmDialogProps) {
  const [disarmDateTime, setDisarmDateTime] = useState<Date | undefined>();
  const { toast } = useToast();
  const [selectedQuick, setSelectedQuick] = useState<number | null>(null);

  const quickOptions = [
    { label: '15 min', minutes: 15 },
    { label: '30 min', minutes: 30 },
    { label: '60 min', minutes: 60 },
    { label: '2 hours', minutes: 120 },
  ];

  const handleDisarm = async () => {
    if (!disarmDateTime) {
      toast({
        title: 'Error',
        description: 'Please select a valid date and time for disarming.',
        variant: 'destructive',
      });
      return;
    }

    // Validate the selected date/time is in the future
    if (isBefore(disarmDateTime, new Date())) {
      toast({
        title: 'Invalid time',
        description: 'Please select a future date and time for disarming.',
        variant: 'destructive',
      });
      return;
    }

    await onDisarm(disarmDateTime);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Temporary Disarm</DialogTitle>
          {/* <DialogDescription>
            Select a date and time to automatically re-arm the site.
          </DialogDescription> */}
        </DialogHeader>

        <div className="p-4">
        <div className="flex justify-center gap-2 px-4 pb-4">
          {quickOptions.map((opt) => (
            <Button
              key={opt.minutes}
              variant="outline"
              size="sm"
              onClick={() => {
                setDisarmDateTime(addMinutes(new Date(), opt.minutes));
                setSelectedQuick(opt.minutes);
              }}
              className={selectedQuick === opt.minutes ? 'border-lime bg-lime/20 text-lime' : ''}
            >
              {opt.label}
            </Button>
          ))}
        </div>
          <DateTimePicker
            value={disarmDateTime}
            onChange={(date) => {
              setDisarmDateTime(date);
              setSelectedQuick(null);
            }}
            disabledDays={(date: Date) => isBefore(date, new Date())}
            granularity="minute"
            hourCycle={12}
          />
        </div>

        

        <DialogFooter>
          <Button
            type="button"
            onClick={handleDisarm}
            variant="destructive"
            disabled={isDisarming || !disarmDateTime}
          >
            {isDisarming ? (
              <>
                <Spinner className="mr-2 h-4 w-4" />
                Disarming...
              </>
            ) : (
              'Confirm Disarm'
            )}
          </Button>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isDisarming}>
            Cancel
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
